/* eslint-disable max-len */
import { touch } from 'redux-form';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { tap } from 'rxjs/operators/tap';
import { of } from 'rxjs/observable/of';

import mapValues from 'lodash.mapvalues';

import {
  setCartBillingAddress,
  setCartShippingAddress,
  addAndSetCartShippingAddress,
  addAndSetCartBillingAddress,
  addAndSetAsBothShippingAndBillingAddress,
} from '../../graphql/cart';

import { catchReduxFormMutationErrors } from '../helpers/errors';
import getNextRouteForCartType from '../helpers/getNextRouteForCartType';

const addStreamSideEffects = (apolloClient) => (streams) => {
  const {
    keptInitialAddress,
    changedBillingAddressSelection,
    incompleteForm,
    addBothNewFullAddresses,
    addShippingAndSyncBilling,
    changeShippingAddress,
    syncBillingWithSelectedShipping,
    changeFullDefaultBillingAddress,
    notChangeFullDefaultBillingAddress,
    notChangeShippingAddress,
    addShippingAddress,
    otherJunk,
  } = streams;

  const gotoNextStep = keptInitialAddress.pipe(
    mergeMap(({ isDownloadOnly }) =>
      of(getNextRouteForCartType(isDownloadOnly))
    )
  );

  const handleReduxFormMutationErrors = catchReduxFormMutationErrors(
    'checkout',
    {
      'user.cart.setCartBillingAddress': {
        allowEmptyFields: true,
        prefix: 'billing_address',
      },
      'user.cart.setCartShippingAddress': {
        allowEmptyFields: true,
        prefix: 'shipping_address',
      },
      'user.cart.addAndSetCartBillingAddress': 'billing_address',
      'user.cart.addAndSetCartShippingAddress': 'shipping_address',
      'user.cart.addAndSetAsBothShippingAndBillingAddress': 'shipping_address',
    }
  );

  const stopProgress = incompleteForm.pipe(
    tap(() => {
      window.scrollTo(0, 0);
    }),
    mergeMap(({ errorFields }) => of(touch('checkout', ...errorFields)))
  );

  const doBillingAddressChangeMutation = changedBillingAddressSelection.pipe(
    mergeMap(({ cartId, billingAddress }) =>
      apolloClient.mutate({
        mutation: setCartBillingAddress,
        variables: {
          customerAddressId: billingAddress,
          cartId,
        },
      })
    )
  );

  const doShippingAddAndBillingSyncMutation = addShippingAndSyncBilling.pipe(
    mergeMap(({ cartId, mappedFormValuesForMutation }) => {
      const { shipping } = mappedFormValuesForMutation;

      return apolloClient.mutate({
        mutation: addAndSetAsBothShippingAndBillingAddress,
        variables: {
          cartId,
          address: shipping,
        },
      });
    })
  );

  const doShippingAddressMutation = changeShippingAddress.pipe(
    mergeMap(({ cartId, shippingAddress }) =>
      apolloClient.mutate({
        mutation: setCartShippingAddress,
        variables: {
          customerAddressId: shippingAddress,
          cartId,
        },
      })
    )
  );

  const doBillingAddressSyncMutation = syncBillingWithSelectedShipping.pipe(
    mergeMap(({ cartId, shippingAddress }) =>
      // Only change the ID, since it's a saved address.
      apolloClient.mutate({
        mutation: setCartBillingAddress,
        variables: {
          customerAddressId: shippingAddress,
          cartId,
        },
      })
    )
  );

  const executeAddAndSetCartBillingAddress = ({
    mappedFormValuesForMutation,
    cartId,
  }) => {
    const { billing } = mappedFormValuesForMutation;

    return apolloClient.mutate({
      mutation: addAndSetCartBillingAddress,
      variables: {
        cartId,
        address: billing,
      },
    });
  };

  const executeAddAndSetCartShippingAddressMutation = ({
    mappedFormValuesForMutation,
    cartId,
  }) => {
    const { shipping } = mappedFormValuesForMutation;

    return apolloClient.mutate({
      mutation: addAndSetCartShippingAddress,
      variables: {
        address: shipping,
        cartId,
      },
    });
  };

  const doBillingAddressAddMutation = changeFullDefaultBillingAddress.pipe(
    mergeMap(executeAddAndSetCartBillingAddress)
  );
  const doAddBothNewFullAddressesBillingAddressAddMutation = addBothNewFullAddresses.pipe(
    mergeMap(executeAddAndSetCartBillingAddress)
  );

  const doShippingAddressAddMutation = addShippingAddress.pipe(
    mergeMap(executeAddAndSetCartShippingAddressMutation)
  );
  const doAddBothNewFullAddressesShippingAddressAddMutation = addBothNewFullAddresses.pipe(
    mergeMap(executeAddAndSetCartShippingAddressMutation)
  );

  return mapValues(
    {
      gotoNextStep,
      doBillingAddressChangeMutation,
      stopProgress,
      doAddBothNewFullAddressesBillingAddressAddMutation,
      doAddBothNewFullAddressesShippingAddressAddMutation,
      doShippingAddAndBillingSyncMutation,
      doBillingAddressAddMutation,
      notChangeFullDefaultBillingAddress,
      notChangeShippingAddress,
      doShippingAddressMutation,
      doBillingAddressSyncMutation,
      doShippingAddressAddMutation,
      otherJunk,
    },
    (val) => val.pipe(handleReduxFormMutationErrors)
  );
};

export default addStreamSideEffects;
